import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider, useLang } from "@/i18n";
import Seo from "@/components/Seo";
import Nav from "@/components/Nav";
import Landing from "@/pages/Landing";
import BookService from "@/pages/BookService";
import PriorityService from "@/pages/PriorityService";
import Membership from "@/pages/Membership";
import TrackStatus from "@/pages/TrackStatus";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import SampleReport from "@/pages/SampleReport";
import ReportView from "@/pages/ReportView";
import ClientPortal from "@/pages/ClientPortal";
import ServicePage from "@/pages/ServicePage";
import LocationPage from "@/pages/LocationPage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import ProblemPage from "@/pages/ProblemPage";
import RequestReceived from "@/pages/RequestReceived";
import TheDoor from "@/pages/TheDoor";
import AutosLanding from "@/pages/AutosLanding";
import AutosQuote from "@/pages/AutosQuote";
import AutosMemberships from "@/pages/AutosMemberships";
import Garaje from "@/pages/Garaje";
import ServiceTracker from "@/pages/ServiceTracker";
import TecnicoView from "@/pages/TecnicoView";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function LandingRoute({ lang }) {
  const { setLang } = useLang();
  useEffect(() => { setLang(lang); }, [lang, setLang]);
  return (
    <>
      <Seo lang={lang} />
      <Landing />
    </>
  );
}

function Localized({ lang, children }) {
  const { setLang } = useLang();
  useEffect(() => { setLang(lang); }, [lang, setLang]);
  return children;
}

function Shell() {
  const loc = useLocation();
  const hideNav = loc.pathname === "/admin" || loc.pathname === "/portal" || loc.pathname === "/" || loc.pathname === "/en" || loc.pathname === "/garaje" || loc.pathname === "/en/garage" || loc.pathname === "/tecnico" || loc.pathname.startsWith("/servicio/");
  return (
    <>
      <ScrollToTop />
      {!hideNav && <Nav />}
      <Routes>
        <Route path="/" element={<TheDoor lang="es" />} />
        <Route path="/en" element={<TheDoor lang="en" />} />
        <Route path="/aviacion" element={<LandingRoute lang="es" />} />
        <Route path="/en/aviation" element={<LandingRoute lang="en" />} />
        <Route path="/autos" element={<Localized lang="es"><AutosLanding lang="es" /></Localized>} />
        <Route path="/en/cars" element={<Localized lang="en"><AutosLanding lang="en" /></Localized>} />
        <Route path="/autos/cotizar" element={<Localized lang="es"><AutosQuote lang="es" /></Localized>} />
        <Route path="/en/cars/quote" element={<Localized lang="en"><AutosQuote lang="en" /></Localized>} />
        <Route path="/autos/membresias" element={<Localized lang="es"><AutosMemberships lang="es" /></Localized>} />
        <Route path="/en/cars/memberships" element={<Localized lang="en"><AutosMemberships lang="en" /></Localized>} />
        <Route path="/garaje" element={<Localized lang="es"><Garaje lang="es" /></Localized>} />
        <Route path="/en/garage" element={<Localized lang="en"><Garaje lang="en" /></Localized>} />
        <Route path="/book" element={<Localized lang="es"><BookService /></Localized>} />
        <Route path="/en/book" element={<Localized lang="en"><BookService /></Localized>} />
        <Route path="/priority" element={<Localized lang="es"><PriorityService /></Localized>} />
        <Route path="/en/priority" element={<Localized lang="en"><PriorityService /></Localized>} />
        <Route path="/membership" element={<Localized lang="es"><Membership /></Localized>} />
        <Route path="/en/membership" element={<Localized lang="en"><Membership /></Localized>} />
        <Route path="/track" element={<Localized lang="es"><TrackStatus /></Localized>} />
        <Route path="/en/track" element={<Localized lang="en"><TrackStatus /></Localized>} />
        <Route path="/legal" element={<Localized lang="es"><Legal /></Localized>} />
        <Route path="/en/legal" element={<Localized lang="en"><Legal /></Localized>} />
        <Route path="/sample-report" element={<Localized lang="es"><SampleReport /></Localized>} />
        <Route path="/en/sample-report" element={<Localized lang="en"><SampleReport /></Localized>} />
        <Route path="/servicios/:slug" element={<Localized lang="es"><ServicePage lang="es" /></Localized>} />
        <Route path="/en/services/:slug" element={<Localized lang="en"><ServicePage lang="en" /></Localized>} />
        <Route path="/ubicaciones/:slug" element={<Localized lang="es"><LocationPage lang="es" /></Localized>} />
        <Route path="/en/locations/:slug" element={<Localized lang="en"><LocationPage lang="en" /></Localized>} />
        <Route path="/sobre-dukk" element={<Localized lang="es"><AboutPage lang="es" /></Localized>} />
        <Route path="/en/about-dukk" element={<Localized lang="en"><AboutPage lang="en" /></Localized>} />
        <Route path="/preguntas-frecuentes" element={<Localized lang="es"><FaqPage lang="es" /></Localized>} />
        <Route path="/en/faq" element={<Localized lang="en"><FaqPage lang="en" /></Localized>} />
        <Route path="/emergencias/:slug" element={<Localized lang="es"><ProblemPage lang="es" /></Localized>} />
        <Route path="/en/emergency/:slug" element={<Localized lang="en"><ProblemPage lang="en" /></Localized>} />
        <Route path="/guias/:slug" element={<Localized lang="es"><ProblemPage lang="es" /></Localized>} />
        <Route path="/en/guides/:slug" element={<Localized lang="en"><ProblemPage lang="en" /></Localized>} />
        <Route path="/solicitud-recibida" element={<RequestReceived />} />
        <Route path="/servicio/:token" element={<ServiceTracker />} />
        <Route path="/tecnico" element={<TecnicoView />} />
        <Route path="/report/:id" element={<ReportView />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/portal" element={<ClientPortal />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    // Global contact_click tracking — fires once per actual click on a phone,
    // WhatsApp or email link. Event-driven, so React re-renders never duplicate it.
    const handler = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = (a.getAttribute("href") || "").toLowerCase();
      let method = null;
      if (href.startsWith("tel:")) method = "phone";
      else if (href.startsWith("mailto:")) method = "email";
      else if (href.includes("wa.me") || href.includes("whatsapp.com") || href.includes("api.whatsapp")) method = "whatsapp";
      if (!method) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "contact_click", contact_method: method, business: "dukk" });
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
